import React, { FC, ReactNode, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  InputNumber,
  Select,
  Skeleton,
  Typography,
} from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { templateListPath } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/editorShared';
import {
  isJobChainTemplateProblemDetails,
  useJobChainTemplateList,
  useJobChainTemplateMetadata,
  type JobChainTemplateStatus,
  type JobChainTemplateScope,
} from '@/api/JobChainTemplates';
import { faDiagramProject, PageContainer, Title } from '@/ui/components';
import { useTranslate } from '@/translations';
import { TemplateList } from './components/TemplateList';
import {
  templateScopeLabel,
  templateStatusLabel,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/templateLabels';
import {
  clearJobChainTemplateFilters,
  hasJobChainTemplateBusinessFilters,
  parseJobChainTemplateUrlState,
  serializeJobChainTemplateUrlState,
  setJobChainTemplateFilter,
  setJobChainTemplatePagination,
  type JobChainTemplateBusinessFilters,
  type JobChainTemplateUrlState,
} from './urlState';
import {
  EmptyState,
  FailureDetails,
  FilterActions,
  FilterField,
  FilterGrid,
  HeaderCopy,
  HeaderRow,
  ResultsRegion,
} from './JobChainTemplates.styled';

const statusOptions: JobChainTemplateStatus[] = ['Draft', 'Active', 'Inactive'];

const fallbackOption = (value: string | number | undefined) =>
  value === undefined ? [] : [{ value, label: String(value) }];

export const JobChainTemplates: FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseJobChainTemplateUrlState(searchParams),
    [searchParams],
  );
  const templateQuery = useJobChainTemplateList(urlState);
  const metadataQuery = useJobChainTemplateMetadata();
  const metadata = metadataQuery.data;

  const writeUrlState = (next: JobChainTemplateUrlState) => {
    setSearchParams(serializeJobChainTemplateUrlState(next));
  };

  const updateFilter = (patch: JobChainTemplateBusinessFilters) => {
    writeUrlState(setJobChainTemplateFilter(urlState, patch));
  };

  const scopeOptions = metadata
    ? metadata.authorizedScopes.map((scope) => ({
        value: scope,
        label: templateScopeLabel(scope, t),
      }))
    : urlState.scope
      ? [
          {
            value: urlState.scope,
            label: templateScopeLabel(urlState.scope, t),
          },
        ]
      : [];
  const customerOptions = metadata
    ? metadata.customers.map((customer) => ({
        value: customer.id,
        label: customer.name,
      }))
    : fallbackOption(urlState.customerId);
  const typeOptions = metadata
    ? metadata.jobChainTypes.map((jobChainType) => ({
        value: jobChainType.value,
        label: jobChainType.label,
      }))
    : fallbackOption(urlState.jobChainType);
  const canCreate = Boolean(
    metadata?.capabilities.canCreateGlobal ||
      metadata?.capabilities.canCreateCustomer,
  );
  const failure = metadataQuery.isError
    ? metadataQuery.error
    : templateQuery.isError
      ? templateQuery.error
      : null;
  const problemDetails =
    failure && isJobChainTemplateProblemDetails(failure.message)
      ? failure.message
      : null;

  const renderFailure = () => {
    const title = problemDetails?.title || t('jobChainTemplatesFailureTitle');
    const detail =
      problemDetails?.detail || t('jobChainTemplatesFailureDetail');
    const description: ReactNode = (
      <FailureDetails>
        <span>{detail}</span>
        {problemDetails?.code ? (
          <span>
            {t('jobChainTemplatesProblemCode', {
              code: problemDetails.code,
            })}
          </span>
        ) : null}
        {problemDetails?.correlationId ? (
          <span>
            {t('jobChainTemplatesCorrelationId', {
              correlationId: problemDetails.correlationId,
            })}
          </span>
        ) : null}
      </FailureDetails>
    );

    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message={title}
          description={description}
          action={
            <Button
              type="primary"
              onClick={() => {
                void metadataQuery.refetch();
                void templateQuery.refetch();
              }}
            >
              {t('jobChainTemplatesRetry')}
            </Button>
          }
        />
      </Card>
    );
  };

  const renderResults = () => {
    if (failure) return renderFailure();

    if (!metadata || !templateQuery.data) {
      return (
        <Card>
          <div aria-label={t('jobChainTemplatesLoading')}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </Card>
      );
    }

    if (templateQuery.data.total === 0) {
      const filtered = hasJobChainTemplateBusinessFilters(urlState);
      return (
        <Card>
          <EmptyState>
            <Typography.Title level={4}>
              {t(
                filtered
                  ? 'jobChainTemplatesFilteredEmptyTitle'
                  : 'jobChainTemplatesSystemEmptyTitle',
              )}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t(
                filtered
                  ? 'jobChainTemplatesFilteredEmptyDetail'
                  : 'jobChainTemplatesSystemEmptyDetail',
              )}
            </Typography.Text>
          </EmptyState>
        </Card>
      );
    }

    return (
      <TemplateList
        data={templateQuery.data}
        loading={templateQuery.isFetching || metadataQuery.isFetching}
        page={urlState.page}
        pageSize={urlState.pageSize}
        onPaginationChange={(page, pageSize) =>
          writeUrlState(setJobChainTemplatePagination(urlState, page, pageSize))
        }
      />
    );
  };

  return (
    <PageContainer>
      <HeaderRow>
        <HeaderCopy>
          <Title title={t('jobChainTemplatesTitle')} icon={faDiagramProject} />
          <Typography.Text type="secondary">
            {t('jobChainTemplatesDescription')}
          </Typography.Text>
        </HeaderCopy>
        {canCreate ? (
          <Button
            type="primary"
            onClick={() => navigate(`${templateListPath}/new${search}`)}
          >
            {t('jobChainTemplatesNewTemplate')}
          </Button>
        ) : null}
      </HeaderRow>

      <Card>
        <FilterGrid>
          <FilterField>
            <span>{t('jobChainTemplatesFilterScope')}</span>
            <Select<JobChainTemplateScope>
              aria-label={t('jobChainTemplatesFilterScope')}
              allowClear
              virtual={false}
              placeholder={t('jobChainTemplatesAllOptions')}
              disabled={!metadata}
              options={scopeOptions}
              value={urlState.scope}
              onChange={(scope) => updateFilter({ scope })}
            />
          </FilterField>
          <FilterField>
            <span>{t('jobChainTemplatesFilterCustomer')}</span>
            <Select<number>
              aria-label={t('jobChainTemplatesFilterCustomer')}
              allowClear
              virtual={false}
              placeholder={t('jobChainTemplatesAllOptions')}
              disabled={!metadata || urlState.scope !== 'Customer'}
              options={customerOptions}
              value={urlState.customerId}
              onChange={(customerId) => updateFilter({ customerId })}
            />
          </FilterField>
          <FilterField>
            <span>{t('jobChainTemplatesFilterType')}</span>
            <Select<string>
              aria-label={t('jobChainTemplatesFilterType')}
              allowClear
              virtual={false}
              placeholder={t('jobChainTemplatesAllOptions')}
              disabled={!metadata}
              options={typeOptions}
              value={urlState.jobChainType}
              onChange={(jobChainType) => updateFilter({ jobChainType })}
            />
          </FilterField>
          <FilterField>
            <span>{t('jobChainTemplatesFilterStatus')}</span>
            <Select<JobChainTemplateStatus>
              aria-label={t('jobChainTemplatesFilterStatus')}
              allowClear
              virtual={false}
              placeholder={t('jobChainTemplatesAllOptions')}
              options={statusOptions.map((status) => ({
                value: status,
                label: templateStatusLabel(status, t),
              }))}
              value={urlState.status}
              onChange={(status) => updateFilter({ status })}
            />
          </FilterField>
          <FilterField>
            <span>{t('jobChainTemplatesFilterVersion')}</span>
            <InputNumber
              aria-label={t('jobChainTemplatesFilterVersion')}
              min={1}
              precision={0}
              value={urlState.version}
              onChange={(version) =>
                updateFilter({ version: version ?? undefined })
              }
            />
          </FilterField>
        </FilterGrid>
        {hasJobChainTemplateBusinessFilters(urlState) ? (
          <FilterActions>
            <Button
              onClick={() =>
                writeUrlState(clearJobChainTemplateFilters(urlState))
              }
            >
              {t('jobChainTemplatesClearFilters')}
            </Button>
          </FilterActions>
        ) : null}
      </Card>

      <ResultsRegion aria-live="polite">{renderResults()}</ResultsRegion>
    </PageContainer>
  );
};
